// ink-component
import React, { ThHTMLAttributes } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { FeedbackVariants } from '../../../_common/Types/GlobalTypes';
import { useCachedFrozenCellSettings } from '../../hooks/freeze';
import CoreCell from '../../_common/CoreCell';
import { styles, cellPresets } from '../../_common/styles';

export type NewTableCellProps = ThHTMLAttributes<HTMLTableCellElement> & {
  /** Align text to left or right */
  align?: 'left' | 'right';
  /** Set the width of the cell, string with unit (px or %), e.g. '10px' or '10%' */
  width?: string;
  /** Apply special style properties for certain child types inside the cell */
  preset?:
    | 'action'
    | 'checkbox'
    | 'dragHandleIcon'
    | 'editable'
    | 'key'
    | 'lowContrast'
    | 'radio'
    | 'totals'
    | 'twiddleIcon';
  /** Semantic variants, based on intent */
  variant?: Extract<
    FeedbackVariants,
    'feedback-positive' | 'feedback-notice' | 'feedback-negative' | 'feedback-neutral' | 'feedback-informational'
  >;
  /** Use only when `preset` and `variant` prove insufficient. Sets the background color of the cell. See Usage for a list of available colors. (This overwrides the `bgColor` of [NewTableCell](/components/NewTable.Cell).)  */
  bgColor?: string;
  /** Set breaking of the text in multiple lines */
  whiteSpace?: 'normal' | 'nowrap';
  /** Freezes the cell to the left when the table is scrolled. You should **always**
   * pass this prop to the **header and all the cells in the same column**.
   * If you want to freeze **more than one column** in a table, make sure you freeze them
   * **in a sequence**. From the first column to the last one you want to freeze,
   * without skipping any cell or header in between. */
  freeze?: boolean | React.CSSProperties['left'];
  /** Adds a dark blue border to the left or right side */
  divider?: 'left' | 'right';
};

/* @internal */
export const StyledCell = styled(CoreCell)<NewTableCellProps>`
  ${styles['cell']}
  ${({ theme }) => cellPresets[theme.cellPreset]};
  ${styles['orderableCell']}
`;

/**
 * @group NewTable
 * @purpose Unit of data inside a table row
 * @categories table
 * @HTMLType HTMLTableCellElement
 */
const NewTableCell = (props: NewTableCellProps) => {
  const {
    align,
    variant,
    bgColor,
    className,
    divider,
    freeze,
    preset,
    style,
    whiteSpace,
    width,
    ...otherProps
  } = props;

  const { offsetLeft, lastFrozenColumn, cellRef } = useCachedFrozenCellSettings();

  return (
    <ThemeProvider
      theme={rowTheme => ({
        ...rowTheme,
        cellAlign: align,
        cellVariant: variant,
        cellBgColor: bgColor || rowTheme?.rowBgColor,
        cellPreset: preset || rowTheme?.rowPreset,
        cellDivider: divider,
        cellFreeze: !!freeze,
        cellLastFrozenColumn: lastFrozenColumn,
        cellOffsetLeft: typeof freeze === 'boolean' ? `${offsetLeft}px` : freeze,
        cellWhiteSpace: whiteSpace,
        cellWidthValue: props.width,
        // width was left here because it prevents Typeahead from applying 300px
        width,
      })}
    >
      <StyledCell
        as={preset === 'key' ? 'th' : 'td'}
        data-freeze={freeze ? '' : undefined}
        {...otherProps}
        ref={cellRef}
      />
    </ThemeProvider>
  );
};

NewTableCell.defaultProps = {
  align: 'left',
  bgColor: undefined,
  divider: undefined,
  freeze: false,
  preset: undefined,
  whiteSpace: 'normal',
  width: undefined,
};

NewTableCell.displayName = 'NewTable.Cell';

export default NewTableCell;
